import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Loader2, X } from 'lucide-react';
import type { CreatePrestataireInput } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal CRUD prestataire (Phase 11 — sous-module Interventions).
 *
 *  - Création ou édition selon `prestataireId` fourni.
 *  - Les sous-champs `contact.email`, `contact.telephone`, `contact.adresse`
 *    sont saisis séparément côté UI mais regroupés sous la clé `contact` JSON
 *    dans le payload soumis au backend.
 */

interface PrestataireDetail {
  id: string;
  nom: string;
  specialite: string | null;
  contact: {
    email?: string | null;
    telephone?: string | null;
    adresse?: string | null;
  } | null;
  is_active: boolean;
  custom_fields: Record<string, unknown>;
}

interface FormValues {
  nom: string;
  specialite: string;
  email: string;
  telephone: string;
  adresse: string;
  is_active: boolean;
}

export function PrestataireFormModal({
  prestataireId,
  onClose,
  onSaved,
  onDeleted,
}: {
  prestataireId?: string;
  onClose(): void;
  onSaved(): void;
  onDeleted?(): void;
}) {
  const isEdit = !!prestataireId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      nom: '',
      specialite: '',
      email: '',
      telephone: '',
      adresse: '',
      is_active: true,
    },
  });

  useEffect(() => {
    if (!prestataireId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get<{ data: PrestataireDetail }>(`/prestataires/${prestataireId}`);
        const d = res.data.data;
        reset({
          nom: d.nom,
          specialite: d.specialite ?? '',
          email: d.contact?.email ?? '',
          telephone: d.contact?.telephone ?? '',
          adresse: d.contact?.adresse ?? '',
          is_active: d.is_active,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [prestataireId, reset]);

  async function onSubmit(values: FormValues) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const contact = {
        email: values.email.trim() || null,
        telephone: values.telephone.trim() || null,
        adresse: values.adresse.trim() || null,
      };
      const payload: CreatePrestataireInput = {
        nom: values.nom.trim(),
        specialite: values.specialite.trim() || null,
        contact,
        is_active: values.is_active,
      };
      if (isEdit) {
        await api.patch(`/prestataires/${prestataireId}`, payload);
      } else {
        await api.post('/prestataires', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!prestataireId) return;
    if (!confirm('Désactiver ce prestataire ? (soft delete : is_active=false)')) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.delete(`/prestataires/${prestataireId}`);
      onDeleted?.();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Modifier le prestataire' : 'Nouveau prestataire'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="prestataire-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Nom *" error={errors.nom?.message} className="md:col-span-2">
                    <input
                      {...register('nom', { required: 'Nom requis' })}
                      className={inputClass}
                      placeholder="ACME Maintenance"
                    />
                  </Field>
                  <Field
                    label="Spécialité"
                    className="md:col-span-2"
                    error={errors.specialite?.message}
                  >
                    <input
                      {...register('specialite')}
                      className={inputClass}
                      placeholder="Plomberie, électricité…"
                    />
                  </Field>
                </div>

                <fieldset className="border border-gray-200 rounded-lg p-3 mt-2">
                  <legend className="px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Contact
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Email" error={errors.email?.message}>
                      <input
                        type="email"
                        {...register('email')}
                        className={inputClass}
                        placeholder="contact@prestataire.com"
                      />
                    </Field>
                    <Field label="Téléphone" error={errors.telephone?.message}>
                      <input
                        type="tel"
                        {...register('telephone')}
                        className={inputClass}
                        placeholder="+33 1 23 45 67 89"
                      />
                    </Field>
                    <Field
                      label="Adresse"
                      className="md:col-span-2"
                      error={errors.adresse?.message}
                    >
                      <textarea
                        {...register('adresse')}
                        rows={2}
                        className={inputClass}
                        placeholder="12 rue de la Paix, 75001 Paris"
                      />
                    </Field>
                  </div>
                </fieldset>

                <label className="flex items-center space-x-2 text-sm text-gray-700 mt-2">
                  <input
                    type="checkbox"
                    {...register('is_active')}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Actif (utilisable lors d'une affectation externe)</span>
                </label>
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && onDeleted && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Désactiver
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="prestataire-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
